/**
 * A/B Testing Utility
 * Handles variant selection, tracking, and rendering logic for A/B testing
 */

import React from 'react';
import { ABT_COOKIE_PREFIX, AB_TESTING_CAMPAIGNS } from '../constants';
import { getCookie } from './index';

/**
 * Check if OneTrust consent is granted for a specific group
 * @param {string} consentGroup - OneTrust consent group to check
 * @returns {boolean} Whether consent is granted
 */
const hasOneTrustConsent = (consentGroup) => {
  if (typeof window.OnetrustActiveGroups === 'undefined') {
    return false;
  }
  return window.HONDAPS_ENABLE_AB_RAQ && window.OnetrustActiveGroups.includes(consentGroup);
};

/**
 * Wait for OneTrust consent with timeout
 * @param {string} consentGroup - OneTrust consent group to check
 * @param {number} timeout - Maximum time to wait in milliseconds (default: 3000ms)
 * @returns {Promise<boolean>} Promise that resolves with consent status
 */
const waitForOneTrustConsent = (consentGroup, timeout = 3000) => new Promise((resolve) => {
  const startTime = Date.now();

  const checkConsent = () => {
    // Check if OneTrust is initialized and consent is granted
    if (hasOneTrustConsent(consentGroup)) {
      resolve(true);
      return;
    }

      // Check if timeout has been reached
    if (Date.now() - startTime >= timeout) {
      resolve(false);
      return;
    }

      // Continue checking every 200ms
    setTimeout(checkConsent, 200);
  };

  checkConsent();
});

/**
 * Get a variant for a specific campaign
 * @param {string} campaignName - Name of the campaign for A/B testing
 * @param {string} defaultVariant - Variant to show if consent is not granted
 * @returns {Promise<string>} Selected variant or default variant if consent not granted
 */
const getVariant = async (campaignName, defaultVariant = '') => {
  const campaignModule = Object.values(AB_TESTING_CAMPAIGNS).find((campaign) => campaign.campaignName === campaignName);
  if (!campaignModule) {
    throw new Error(`Campaign ${campaignName} not found`);
  }
  const { variants, consentGroup } = campaignModule;

  // Wait for OneTrust consent (up to 3 seconds)
  const hasConsent = await waitForOneTrustConsent(consentGroup);

  if (!hasConsent) {
    return defaultVariant;
  }

  const cookieName = `${ABT_COOKIE_PREFIX}_${campaignName}`;
  const selectedVariant = getCookie(cookieName);

  if (!selectedVariant || !Object.values(variants).includes(selectedVariant)) {
    return defaultVariant;
  }

  return selectedVariant;
};

/**
 * Get variant experience
 * @param {string} campaignName - Name of the campaign for A/B testing
 * @returns {Promise<string>} Variant experience
 */
const getVariantExperience = async (campaignName) => {
  const campaignModule = Object.values(AB_TESTING_CAMPAIGNS).find((campaign) => campaign.campaignName === campaignName);
  if (!campaignModule) {
    return '';
  }
  const { experienceKey } = campaignModule;
  const variant = await getVariant(campaignName);
  return variant ? `${experienceKey} | ${variant}` : '';
};

/**
 * Get variant experience
 * @param {string} campaignName - Name of the campaign for A/B testing
 * @returns {Promise<string>} Variant experience
 */
const getVariantExperienceWithoutWaiting = (campaignName) => {
  const campaignModule = Object.values(AB_TESTING_CAMPAIGNS).find((campaign) => campaign.campaignName === campaignName);
  if (!campaignModule) {
    return '';
  }
  const { variants, consentGroup, experienceKey } = campaignModule;

  const hasConsent = hasOneTrustConsent(consentGroup);
  if (!hasConsent) {
    return '';
  }

  const cookieName = `${ABT_COOKIE_PREFIX}_${campaignName}`;
  const selectedVariant = getCookie(cookieName);

  if (!selectedVariant || !Object.values(variants).includes(selectedVariant)) {
    return '';
  }

  return `${experienceKey} | ${selectedVariant}`;
};

/**
 * Render content based on variant (Vanilla JS version)
 * @param {string} campaignName - Name of the campaign
 * @param {Object} variants - Object mapping variants to content
 * @param {HTMLElement} container - Container element to render content
 * @param {string} defaultVariant - Variant to show if consent is not granted
 */
const renderVariant = async (campaignName, variants, container, defaultVariant) => {
  const selectedVariant = await getVariant(campaignName, defaultVariant);
  const content = variants[selectedVariant];
  if (!container) {
    return;
  }
  if (typeof content === 'string') {
    container.innerHTML = content;
  } else if (typeof content === 'function') {
    container.innerHTML = content();
  }
};

/**
 * Render content based on variant (Vanilla JS version)
 * @param {string} campaignName - Name of the campaign
 * @param {Object} variants - Object mapping variants to content
 * @param {HTMLElement} container - Container element to render content
 * @param {string} defaultVariant - Variant to show if consent is not granted
 */
const renderVariantStyle = (campaignName, variants, container, defaultVariant) => {
  const changeContainerStyle = async () => {
    const selectedVariant = await getVariant(campaignName, defaultVariant);
    const content = variants[selectedVariant];
    if (!container) {
      return;
    }
    if (typeof content === 'string') {
      container.classList.remove(...container.classList);
      container.classList.add(...content.split(' '));
    } else if (typeof content === 'function') {
      container.classList.remove(...container.classList);
      container.classList.add(...content().split(' '));
    }
  };
  changeContainerStyle();
  window.addEventListener('OneTrustGroupsUpdated', changeContainerStyle);
};

/**
 * React component for A/B testing
 * @param {Object} props
 * @param {string} props.campaignName - Name of the campaign
 * @param {Object} props.variants - Object mapping variants to React components
 * @param {string} props.defaultVariant - Variant to show if consent is not granted
 * @param {boolean} props.loading - Whether to show loading state
 * @returns {JSX.Element} Selected variant component or default component
 */
const ABTestComponent = ({
  campaignName,
  variants,
  defaultVariant,
  loading,
}) => {
  const [selectedVariant, setSelectedVariant] = React.useState(defaultVariant);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const loadVariant = async () => {
      try {
        const variant = await getVariant(campaignName, defaultVariant);
        setSelectedVariant(variant);
      } catch (error) {
        setSelectedVariant(defaultVariant);
      } finally {
        setIsLoading(false);
      }
    };

    window.addEventListener('OneTrustGroupsUpdated', loadVariant);
    loadVariant();
    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', loadVariant);
    };
  }, [campaignName, defaultVariant, loading]);

  if (isLoading || loading) {
    return '';
  }

  return variants[selectedVariant];
};

export {
  getVariant,
  getVariantExperience,
  getVariantExperienceWithoutWaiting,
  renderVariant,
  renderVariantStyle,
  ABTestComponent,
};
